import {
  Grid,
  Box,
  Typography,
  Button,
  List,
  ListItemButton,
  ListItemText,
  Chip,
  Stack,
  ListItemIcon,
  Alert,
  AlertTitle,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Trans, useTranslation } from 'react-i18next';
import {
  ActionSheet,
  ActionSheetButton,
  ActionSheetButtonStyle,
} from '@capacitor/action-sheet';
import { Capacitor } from '@capacitor/core';
import { LocationOperator } from 'app/pages/Home/LocationContent/LocationOperator';
import { Location } from 'features/locations/location.types';
import { GeoEventMetadataText } from 'features/geoEvents/components/GeoEventMetadataRows';
import { getDateFormatted, getTimeAgo } from 'shared/dates';
import { ENV } from 'shared/ENV';
import { getDeviceInfo } from 'state/localStorageTyped';
import { RichTextContent } from 'components/RichTextContent';
import { Helmet } from 'react-helmet';
import { MapEntityDetailsLayout } from 'features/mapping/components/MapEntityDetailsLayout';
import { useGeoEvent } from 'features/geoEvents/hooks/useGeoEvent';
import { InlineStatus } from 'components/InlineStatus';
import { useShareOrCopy } from 'hooks/useShareOrCopy';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { useHoursDrawerState } from 'features/locations/hooks/useHoursDrawerState';
import { LatLng } from 'shared/types';
import { useLocationV2Enabled } from 'features/locations/hooks/useLocationV2Enabled';
import { getLocationTypeTransKey } from 'features/locations/location.constants';
import { GeoEventLinkItem } from 'features/geoEvents/components/GeoEventLinks';
import { useTabs } from 'hooks/useTabs';
import { useSelectedLocationGeoEventId } from 'features/locations/hooks/useSelectedLocationGeoEventId';
import { useInternalIncidentTab } from 'features/geoEvents/hooks/useInternalIncidentTab';
import { getCurrentHourStatus } from './getCurrentHourStatus';

const openAppleMaps = (coords: LatLng): void => {
  window.open(
    `https://maps.apple.com/?q=${coords.lat},${coords.lng}`,
    '_blank',
  );
};

const openGoogleMaps = (coords: LatLng): void => {
  window.open(
    `https://www.google.com/maps/search/?api=1&query=${coords.lat},${coords.lng}`,
    '_blank',
  );
};

const openWazeApp = (coords: LatLng): void => {
  window.open(
    `https://www.waze.com/ul?q=${coords.lat},${coords.lng}`,
    '_blank',
  );
};

const useStyles = makeStyles()((theme) => ({
  sectionHeader: {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  message: {
    whiteSpace: 'pre-line',
    wordBreak: 'break-word',
    lineHeight: theme.typography.body1.lineHeight,
  },
}));

const TAB_PANEL_ID_PREFIX = 'location-details';

export const LocationDetails = (): JSX.Element => {
  const geoEventId = useSelectedLocationGeoEventId();
  const {
    geoEvent: location,
    isGeoEventLoading,
    isGeoEventFetchError,
  } = useGeoEvent<Location>({
    id: geoEventId,
    useCacheBuster: true,
  });
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { canShareOrCopy, shareOrCopyUrl } = useShareOrCopy();
  const showLocationV2 = useLocationV2Enabled();
  const { setOpen: setHoursDrawerOpen } = useHoursDrawerState();
  const internalTab = useInternalIncidentTab(location);

  const deviceInfo = getDeviceInfo();

  const handleDirectionsClick = async (): Promise<void> => {
    if (!location) {
      return;
    }

    let options: ActionSheetButton[] = [
      { title: t('location.directions.openGoogle') },
      { title: t('location.directions.openWaze') },
      { title: t('common.cancel'), style: ActionSheetButtonStyle.Cancel },
    ];

    const iosPlatform = deviceInfo?.platform === 'ios';

    if (iosPlatform) {
      options = [{ title: t('location.directions.openApple') }, ...options];
    }

    const { index } = await ActionSheet.showActions({
      title: Capacitor.isNativePlatform()
        ? undefined
        : t('location.directions.title'),
      options,
    });

    const latLng: LatLng = { lat: location.lat, lng: location.lng };

    if (index === 0) {
      if (iosPlatform) {
        openAppleMaps(latLng);
        return;
      }
      openGoogleMaps(latLng);
      return;
    }

    if (index === 1) {
      if (iosPlatform) {
        openGoogleMaps(latLng);
        return;
      }
      openWazeApp(latLng);
      return;
    }

    if (index === 2 && iosPlatform) {
      openWazeApp(latLng);
    }
  };

  const handleShare = (): void => {
    if (!location) {
      return;
    }

    shareOrCopyUrl({
      title: location.name,
      text: location.name,
      url: `${ENV.VITE_BASE_SHARE_URL}location/${location.id}`,
      androidDialogTitle: t('location.shareLocation'),
    });
  };

  const hasInformation = Boolean(location?.data.information);
  const hasLinks = (location?.data.links?.length ?? 0) > 0;
  const hasTabContent = hasInformation || hasLinks;

  const tabData = [
    {
      tabId: 'information',
      label: t('location.sections.information'),
      enabled: true,
      content: (
        <>
          {location?.data.information && (
            <Grid size={12} sx={{ mx: 2 }}>
              <RichTextContent
                content={location.data.information}
                className={classes.message}
              />
            </Grid>
          )}

          {hasLinks && (
            <>
              <Grid size={12} className={classes.sectionHeader} sx={{ p: 2 }}>
                <Typography variant="h3">
                  {t('location.sections.links')}
                </Typography>
              </Grid>

              <Grid size={12}>
                <List>
                  {location?.data.links?.map((link) => (
                    <GeoEventLinkItem
                      key={`${link.label}-${link.value}`}
                      link={link}
                    />
                  ))}
                </List>
              </Grid>
            </>
          )}

          <LocationOperator location={location} />
        </>
      ),
    },
    internalTab,
  ];

  const { tabsProps, tabProps, tabPanels } = useTabs({
    idPrefix: TAB_PANEL_ID_PREFIX,
    ariaLabel: t('geoEvent.tabsLabel'),
    tabData,
  });

  if (isGeoEventLoading || isGeoEventFetchError || !location) {
    return <InlineStatus status={isGeoEventLoading ? 'loading' : 'error'} />;
  }

  const currentHourStatus = getCurrentHourStatus(
    t,
    location.data.hours,
    location.data.timezone,
  );

  const buttonsSectionBottomPadding =
    hasTabContent && !location.data.hours.length && !location.data.atCapacity
      ? 3
      : 2;

  const atCapacitySectionBottomPadding =
    hasTabContent && !location.data.hours.length ? 3 : 2;

  const orgName =
    location.data.organization?.displayName ||
    location.createdByName.displayNameShort ||
    t('location.watchDuty');

  return (
    <>
      <Helmet>
        <title>{t('home.locationTitle', { name: location.name })}</title>
      </Helmet>

      <MapEntityDetailsLayout.Root>
        <MapEntityDetailsLayout.Header
          title={location.name}
          subtitle={location.address}
        />

        <MapEntityDetailsLayout.Body pb={buttonsSectionBottomPadding}>
          <MapEntityDetailsLayout.Meta>
            <Typography variant="body2">
              <Trans
                i18nKey="location.updated"
                values={{ timeAgo: getTimeAgo(location.dateModified) }}
              >
                Updated <b>5 minutes ago</b>
              </Trans>
            </Typography>

            <GeoEventMetadataText>
              <Trans
                i18nKey="location.createdBy"
                values={{
                  name: orgName,
                  date: getDateFormatted(location.dateCreated),
                }}
              >
                Created by <b>Watch Duty</b> · Aug 4, 12:54 PM
              </Trans>
            </GeoEventMetadataText>
          </MapEntityDetailsLayout.Meta>

          <MapEntityDetailsLayout.Actions>
            <Button
              fullWidth
              color="neutral"
              variant="outlined"
              size="large"
              onClick={handleDirectionsClick}
            >
              {t('location.buttons.directions')}
            </Button>

            {canShareOrCopy && (
              <Button
                fullWidth
                color="neutral"
                variant="outlined"
                size="large"
                onClick={handleShare}
              >
                {t('location.buttons.share')}
              </Button>
            )}
          </MapEntityDetailsLayout.Actions>
        </MapEntityDetailsLayout.Body>

        {location.data.atCapacity && (
          <MapEntityDetailsLayout.Body pb={atCapacitySectionBottomPadding}>
            <Alert severity="error">
              <AlertTitle mb={0}>
                {t('location.typeAtCapacity', {
                  locationType: t(
                    getLocationTypeTransKey(
                      location.data.locationType,
                      showLocationV2,
                    ),
                  ),
                })}
              </AlertTitle>
            </Alert>
          </MapEntityDetailsLayout.Body>
        )}

        {location.data?.hours.length > 0 && (
          <>
            {/* Full-bleed: the divider border must reach both edges. */}
            <Box className={classes.sectionHeader} sx={{ p: 2 }}>
              <Typography variant="h3">
                {t('location.sections.hours')}
              </Typography>
            </Box>

            <Box sx={{ pb: hasTabContent ? 3 : undefined }}>
              <List>
                <ListItemButton onClick={() => setHoursDrawerOpen(true)}>
                  <ListItemText
                    primary={
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        component="span"
                      >
                        <Chip
                          label={currentHourStatus.statusLabel}
                          variant="watchduty"
                          color={
                            currentHourStatus.status === 'open'
                              ? 'success'
                              : 'error'
                          }
                        />

                        {currentHourStatus.label}
                      </Stack>
                    }
                  />
                  <ListItemIcon
                    sx={(theme) => ({
                      minWidth: 'fit-content',
                      color: theme.palette.text.primary,
                    })}
                  >
                    <KeyboardArrowRightIcon />
                  </ListItemIcon>
                </ListItemButton>
              </List>
            </Box>
          </>
        )}

        {hasTabContent && (
          <MapEntityDetailsLayout.Tabs
            tabsProps={tabsProps}
            tabProps={tabProps}
            tabPanels={tabPanels}
          />
        )}
      </MapEntityDetailsLayout.Root>
    </>
  );
};

LocationDetails.displayName = 'LocationDetails';
